import React, { useState, useEffect } from 'react';
import { 
  DollarSign, Plus, Pencil, Trash2, CheckCircle2, 
  AlertCircle, Tag, Settings, Clock, Calculator,
  Percent, Minus, Play, Pause, Search, Filter
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Switch } from '../../ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Textarea } from '../../ui/textarea';
import { db } from '../../../lib/db';
import { EARNING_TYPES, DEDUCTION_TYPES, formatUSDCurrency } from '../../../lib/usPayrollCalculations';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';
import { useConfirmDialog } from '../../shared/ConfirmDialog';

const DEFAULT_PAY_CODE = {
  code: '',
  name: '',
  description: '',
  type: 'earning',
  category: 'regular',
  calculationMethod: 'fixed',
  rate: '',
  multiplier: 1,
  taxable: true,
  subjectToFICA: true,
  subjectToFUTA: true,
  includeInW2Box1: true,
  w2BoxCode: '',
  glAccountCode: '',
  status: 'active'
};

const DEFAULT_DEDUCTION_CODE = {
  code: '',
  name: '',
  description: '',
  type: 'deduction',
  category: 'pre_tax',
  calculationMethod: 'fixed',
  amount: '',
  isPercentage: false,
  maxAnnualAmount: '',
  employerMatch: false,
  matchPercentage: '',
  matchMaxPercentage: '',
  taxDeferred: true,
  reducesSSWages: true,
  reducesMedicareWages: false,
  w2BoxCode: '',
  glAccountCode: '',
  status: 'active'
};

const EARNING_CATEGORIES = [
  { value: 'regular', label: 'Regular Pay' },
  { value: 'overtime', label: 'Overtime' },
  { value: 'bonus', label: 'Bonus' },
  { value: 'commission', label: 'Commission' },
  { value: 'tips', label: 'Tips' },
  { value: 'pto', label: 'PTO/Vacation' },
  { value: 'sick', label: 'Sick Pay' },
  { value: 'holiday', label: 'Holiday Pay' },
  { value: 'severance', label: 'Severance' },
  { value: 'other', label: 'Other' }
];

const DEDUCTION_CATEGORIES = [
  { value: 'pre_tax', label: 'Pre-Tax', description: 'Reduces taxable income' },
  { value: 'post_tax', label: 'Post-Tax', description: 'After tax calculation' },
  { value: 'garnishment', label: 'Garnishment', description: 'Court-ordered' },
  { value: 'voluntary', label: 'Voluntary', description: 'Employee-elected' }
];

const CALCULATION_METHODS = [
  { value: 'fixed', label: 'Fixed Amount' },
  { value: 'hourly', label: 'Hourly Rate' },
  { value: 'percentage', label: 'Percentage of Gross' },
  { value: 'per_pay_period', label: 'Per Pay Period' }
];

export default function PayCodeManagement() {
  const { confirm } = useConfirmDialog();
  const [loading, setLoading] = useState(true);
  const [payCodes, setPayCodes] = useState([]);
  const [deductionCodes, setDeductionCodes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const [showPayCodeModal, setShowPayCodeModal] = useState(false);
  const [showDeductionModal, setShowDeductionModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedCode, setSelectedCode] = useState(null);
  
  const [payCodeForm, setPayCodeForm] = useState(DEFAULT_PAY_CODE);
  const [deductionForm, setDeductionForm] = useState(DEFAULT_DEDUCTION_CODE);

  // Prevent double seeding from React StrictMode
  const seedingRef = React.useRef(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      let [payCodeData, deductionData] = await Promise.all([
        db.payCodes.toArray(),
        db.deductionCodes.toArray()
      ]);

      // Deduplicate by code (remove duplicates that may have been created)
      const seenPayCodes = new Set();
      payCodeData = payCodeData.filter(pc => {
        if (seenPayCodes.has(pc.code)) return false;
        seenPayCodes.add(pc.code);
        return true;
      });

      const seenDeductions = new Set();
      deductionData = deductionData.filter(dc => {
        if (seenDeductions.has(dc.code)) return false;
        seenDeductions.add(dc.code);
        return true;
      });

      // If no pay codes exist and not already seeding, create defaults
      if (payCodeData.length === 0 && !seedingRef.current) {
        seedingRef.current = true;
        const defaultPayCodes = Object.entries(EARNING_TYPES).map(([key, type]) => ({
          id: uuidv4(),
          code: key.toUpperCase(),
          name: type.name,
          type: 'earning',
          category: type.category || 'regular',
          calculationMethod: type.isHourly ? 'hourly' : 'fixed',
          multiplier: type.multiplier || 1,
          taxable: type.taxable !== false,
          subjectToFICA: type.subjectToFICA !== false,
          subjectToFUTA: type.subjectToFUTA !== false,
          includeInW2Box1: true,
          status: 'active',
          createdAt: Date.now()
        }));
        await db.payCodes.bulkAdd(defaultPayCodes);
        payCodeData = defaultPayCodes;
      }

      // If no deduction codes exist, create defaults
      if (deductionData.length === 0 && !seedingRef.current) {
        seedingRef.current = true;
        const defaultDeductions = Object.entries(DEDUCTION_TYPES).map(([key, type]) => ({
          id: uuidv4(),
          code: key.toUpperCase(),
          name: type.name,
          type: 'deduction',
          category: type.preTax ? 'pre_tax' : 'post_tax',
          calculationMethod: 'fixed',
          taxDeferred: type.preTax,
          reducesSSWages: type.reducesSSWages || false,
          reducesMedicareWages: type.reducesMedicareWages || false,
          w2BoxCode: type.w2BoxCode || '',
          status: 'active',
          createdAt: Date.now()
        }));
        await db.deductionCodes.bulkAdd(defaultDeductions);
        deductionData = defaultDeductions;
      }

      setPayCodes(payCodeData);
      setDeductionCodes(deductionData);
    } catch (error) {
      console.error('Error loading pay codes:', error);
      toast.error('Failed to load pay codes');
    } finally {
      setLoading(false);
    }
  };

  // Pay Code handlers
  const handlePayCodeSubmit = async (e) => {
    e.preventDefault();

    if (!payCodeForm.code.trim() || !payCodeForm.name.trim()) {
      toast.error('Please enter code and name');
      return;
    }

    try {
      if (isEditing && selectedCode) {
        await db.payCodes.update(selectedCode.id, {
          ...payCodeForm,
          updatedAt: Date.now()
        });
        toast.success('Pay code updated successfully');
      } else {
        // Check for duplicate code
        const existing = payCodes.find(c => c.code.toLowerCase() === payCodeForm.code.toLowerCase());
        if (existing) {
          toast.error('Pay code already exists');
          return;
        }

        await db.payCodes.add({
          id: uuidv4(),
          ...payCodeForm,
          createdAt: Date.now()
        });
        toast.success('Pay code created successfully');
      }

      setShowPayCodeModal(false);
      resetPayCodeForm();
      loadData();
    } catch (error) {
      console.error('Error saving pay code:', error);
      toast.error('Failed to save pay code');
    }
  };

  const handleEditPayCode = (code) => {
    setSelectedCode(code);
    setPayCodeForm({
      code: code.code,
      name: code.name,
      description: code.description || '',
      type: 'earning',
      category: code.category || 'regular',
      calculationMethod: code.calculationMethod || 'fixed',
      rate: code.rate || '',
      multiplier: code.multiplier || 1,
      taxable: code.taxable !== false,
      subjectToFICA: code.subjectToFICA !== false,
      subjectToFUTA: code.subjectToFUTA !== false,
      includeInW2Box1: code.includeInW2Box1 !== false,
      w2BoxCode: code.w2BoxCode || '',
      glAccountCode: code.glAccountCode || '',
      status: code.status || 'active'
    });
    setIsEditing(true);
    setShowPayCodeModal(true);
  };

  const handleDeletePayCode = async (code) => {
    const confirmed = await confirm({
      title: 'Delete Pay Code',
      description: `Delete pay code "${code.name}"?`,
      confirmText: 'Delete',
      variant: 'destructive'
    });
    if (!confirmed) return;

    try {
      await db.payCodes.delete(code.id);
      toast.success('Pay code deleted');
      loadData();
    } catch (error) {
      console.error('Error deleting pay code:', error);
      toast.error('Failed to delete pay code');
    }
  };

  const resetPayCodeForm = () => {
    setPayCodeForm(DEFAULT_PAY_CODE);
    setSelectedCode(null);
    setIsEditing(false);
  };

  // Deduction Code handlers
  const handleDeductionSubmit = async (e) => {
    e.preventDefault();

    if (!deductionForm.code.trim() || !deductionForm.name.trim()) {
      toast.error('Please enter code and name');
      return;
    }

    try {
      if (isEditing && selectedCode) {
        await db.deductionCodes.update(selectedCode.id, {
          ...deductionForm,
          updatedAt: Date.now()
        });
        toast.success('Deduction code updated successfully');
      } else {
        const existing = deductionCodes.find(c => c.code.toLowerCase() === deductionForm.code.toLowerCase());
        if (existing) {
          toast.error('Deduction code already exists');
          return;
        }

        await db.deductionCodes.add({
          id: uuidv4(),
          ...deductionForm,
          createdAt: Date.now()
        });
        toast.success('Deduction code created successfully');
      }

      setShowDeductionModal(false);
      resetDeductionForm();
      loadData();
    } catch (error) {
      console.error('Error saving deduction code:', error);
      toast.error('Failed to save deduction code');
    }
  };

  const handleEditDeduction = (code) => {
    setSelectedCode(code);
    setDeductionForm({
      code: code.code,
      name: code.name,
      description: code.description || '',
      type: 'deduction',
      category: code.category || 'pre_tax',
      calculationMethod: code.calculationMethod || 'fixed',
      amount: code.amount || '',
      isPercentage: code.isPercentage || false,
      maxAnnualAmount: code.maxAnnualAmount || '',
      employerMatch: code.employerMatch || false,
      matchPercentage: code.matchPercentage || '',
      matchMaxPercentage: code.matchMaxPercentage || '',
      taxDeferred: code.taxDeferred !== false,
      reducesSSWages: code.reducesSSWages || false,
      reducesMedicareWages: code.reducesMedicareWages || false,
      w2BoxCode: code.w2BoxCode || '',
      glAccountCode: code.glAccountCode || '',
      status: code.status || 'active'
    });
    setIsEditing(true);
    setShowDeductionModal(true);
  };

  const handleDeleteDeduction = async (code) => {
    const confirmed = await confirm({
      title: 'Delete Deduction Code',
      description: `Delete deduction code "${code.name}"?`,
      confirmText: 'Delete',
      variant: 'destructive'
    });
    if (!confirmed) return;

    try {
      await db.deductionCodes.delete(code.id);
      toast.success('Deduction code deleted');
      loadData();
    } catch (error) {
      console.error('Error deleting deduction code:', error);
      toast.error('Failed to delete deduction code');
    }
  };

  const resetDeductionForm = () => {
    setDeductionForm(DEFAULT_DEDUCTION_CODE);
    setSelectedCode(null);
    setIsEditing(false);
  };

  const filteredPayCodes = payCodes.filter(code =>
    code.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    code.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredDeductions = deductionCodes.filter(code =>
    code.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    code.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Pay Codes & Deductions</h1>
          <p className="text-gray-500 mt-1">Configure earning types and deduction codes</p>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            placeholder="Search codes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 w-64"
          />
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Pay Codes</p>
                <p className="text-2xl font-bold">{payCodes.length}</p>
              </div>
              <DollarSign className="w-8 h-8 text-emerald-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Deduction Codes</p>
                <p className="text-2xl font-bold">{deductionCodes.length}</p>
              </div>
              <Minus className="w-8 h-8 text-red-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Pre-Tax Deductions</p>
                <p className="text-2xl font-bold text-blue-600">
                  {deductionCodes.filter(d => d.category === 'pre_tax').length}
                </p>
              </div>
              <Calculator className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Post-Tax Deductions</p>
                <p className="text-2xl font-bold text-amber-600">
                  {deductionCodes.filter(d => d.category === 'post_tax').length}
                </p>
              </div>
              <Calculator className="w-8 h-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="earnings" className="space-y-4">
        <TabsList>
          <TabsTrigger value="earnings">Earning Codes</TabsTrigger>
          <TabsTrigger value="deductions">Deduction Codes</TabsTrigger>
        </TabsList>

        {/* Earning Codes Tab */}
        <TabsContent value="earnings">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Earning Codes</CardTitle>
                  <CardDescription>Types of pay and compensation</CardDescription>
                </div>
                <Button onClick={() => {
                  resetPayCodeForm();
                  setShowPayCodeModal(true);
                }}>
                  <Plus className="w-4 h-4 mr-2" /> Add Earning Code
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Multiplier</TableHead>
                    <TableHead>Taxable</TableHead>
                    <TableHead>FICA</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPayCodes.map(code => (
                    <TableRow key={code.id}>
                      <TableCell>
                        <Badge variant="outline" className="font-mono">
                          {code.code}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-medium">{code.name}</TableCell>
                      <TableCell className="capitalize">{code.category}</TableCell>
                      <TableCell>
                        {code.multiplier !== 1 ? (
                          <Badge className="bg-amber-100 text-amber-700">
                            {code.multiplier}x
                          </Badge>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        {code.taxable ? (
                          <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                        ) : (
                          <AlertCircle className="w-4 h-4 text-gray-300" />
                        )}
                      </TableCell>
                      <TableCell>
                        {code.subjectToFICA ? (
                          <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                        ) : (
                          <AlertCircle className="w-4 h-4 text-gray-300" />
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={code.status === 'active'
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-gray-100 text-gray-700'
                        }>
                          {code.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button size="sm" variant="ghost" onClick={() => handleEditPayCode(code)}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="text-red-500"
                            onClick={() => handleDeletePayCode(code)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Deduction Codes Tab */}
        <TabsContent value="deductions">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Deduction Codes</CardTitle>
                  <CardDescription>Pre-tax and post-tax deductions</CardDescription>
                </div>
                <Button onClick={() => {
                  resetDeductionForm();
                  setShowDeductionModal(true);
                }}>
                  <Plus className="w-4 h-4 mr-2" /> Add Deduction Code
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>W-2 Box</TableHead>
                    <TableHead>Tax Deferred</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredDeductions.map(code => (
                    <TableRow key={code.id}>
                      <TableCell>
                        <Badge variant="outline" className="font-mono">
                          {code.code}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-medium">{code.name}</TableCell>
                      <TableCell>
                        <Badge className={
                          code.category === 'pre_tax' ? 'bg-blue-100 text-blue-700' :
                          code.category === 'garnishment' ? 'bg-red-100 text-red-700' :
                          'bg-gray-100 text-gray-700'
                        }>
                          {DEDUCTION_CATEGORIES.find(c => c.value === code.category)?.label || code.category}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {code.w2BoxCode ? (
                          <Badge variant="outline">Box {code.w2BoxCode}</Badge>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        {code.taxDeferred ? (
                          <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                        ) : (
                          <AlertCircle className="w-4 h-4 text-gray-300" />
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={code.status === 'active'
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-gray-100 text-gray-700'
                        }>
                          {code.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button size="sm" variant="ghost" onClick={() => handleEditDeduction(code)}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="text-red-500"
                            onClick={() => handleDeleteDeduction(code)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Pay Code Modal */}
      <Dialog open={showPayCodeModal} onOpenChange={setShowPayCodeModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{isEditing ? 'Edit Earning Code' : 'Add Earning Code'}</DialogTitle>
            <DialogDescription>
              Configure earning type settings and tax treatment
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handlePayCodeSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Code *</Label>
                <Input
                  value={payCodeForm.code}
                  onChange={(e) => setPayCodeForm({...payCodeForm, code: e.target.value.toUpperCase()})}
                  placeholder="e.g., REG, OT, BONUS"
                  maxLength={10}
                />
              </div>
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input
                  value={payCodeForm.name}
                  onChange={(e) => setPayCodeForm({...payCodeForm, name: e.target.value})}
                  placeholder="Regular Pay"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select 
                  value={payCodeForm.category} 
                  onValueChange={(v) => setPayCodeForm({...payCodeForm, category: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EARNING_CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Multiplier</Label>
                <Input
                  type="number"
                  step="0.5"
                  min="1"
                  value={payCodeForm.multiplier}
                  onChange={(e) => setPayCodeForm({...payCodeForm, multiplier: parseFloat(e.target.value)})}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={payCodeForm.description}
                onChange={(e) => setPayCodeForm({...payCodeForm, description: e.target.value})}
                placeholder="Optional description"
                rows={2}
              />
            </div>

            <div className="space-y-3 pt-2">
              <div className="flex items-center justify-between">
                <Label>Taxable (Federal Income Tax)</Label>
                <Switch
                  checked={payCodeForm.taxable}
                  onCheckedChange={(v) => setPayCodeForm({...payCodeForm, taxable: v})}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label>Subject to FICA (SS/Medicare)</Label>
                <Switch
                  checked={payCodeForm.subjectToFICA}
                  onCheckedChange={(v) => setPayCodeForm({...payCodeForm, subjectToFICA: v})}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label>Subject to FUTA</Label>
                <Switch
                  checked={payCodeForm.subjectToFUTA}
                  onCheckedChange={(v) => setPayCodeForm({...payCodeForm, subjectToFUTA: v})}
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowPayCodeModal(false)}>
                Cancel
              </Button>
              <Button type="submit">
                {isEditing ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Deduction Code Modal */}
      <Dialog open={showDeductionModal} onOpenChange={setShowDeductionModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{isEditing ? 'Edit Deduction Code' : 'Add Deduction Code'}</DialogTitle>
            <DialogDescription>
              Configure deduction settings and tax treatment
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleDeductionSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Code *</Label>
                <Input
                  value={deductionForm.code}
                  onChange={(e) => setDeductionForm({...deductionForm, code: e.target.value.toUpperCase()})}
                  placeholder="e.g., 401K, HEALTH"
                  maxLength={10}
                />
              </div>
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input
                  value={deductionForm.name}
                  onChange={(e) => setDeductionForm({...deductionForm, name: e.target.value})}
                  placeholder="401(k) Contribution"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select 
                  value={deductionForm.category} 
                  onValueChange={(v) => setDeductionForm({...deductionForm, category: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DEDUCTION_CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>
                        <div>
                          <span>{cat.label}</span>
                          <span className="text-xs text-gray-400 ml-2">{cat.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>W-2 Box Code</Label>
                <Input
                  value={deductionForm.w2BoxCode}
                  onChange={(e) => setDeductionForm({...deductionForm, w2BoxCode: e.target.value})}
                  placeholder="e.g., 12D"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Max Annual Amount (IRS Limit)</Label>
              <Input
                type="number"
                value={deductionForm.maxAnnualAmount}
                onChange={(e) => setDeductionForm({...deductionForm, maxAnnualAmount: e.target.value})}
                placeholder="e.g., 23000 for 401(k)"
              />
            </div>

            <div className="space-y-3 pt-2">
              <div className="flex items-center justify-between">
                <Label>Tax Deferred (Pre-Tax)</Label>
                <Switch
                  checked={deductionForm.taxDeferred}
                  onCheckedChange={(v) => setDeductionForm({...deductionForm, taxDeferred: v})}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label>Reduces Social Security Wages</Label>
                <Switch
                  checked={deductionForm.reducesSSWages}
                  onCheckedChange={(v) => setDeductionForm({...deductionForm, reducesSSWages: v})}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label>Employer Match Available</Label>
                <Switch
                  checked={deductionForm.employerMatch}
                  onCheckedChange={(v) => setDeductionForm({...deductionForm, employerMatch: v})}
                />
              </div>
            </div>

            {deductionForm.employerMatch && (
              <div className="grid grid-cols-2 gap-4 p-3 bg-gray-50 rounded-lg">
                <div className="space-y-2">
                  <Label>Match Percentage</Label>
                  <Input
                    type="number"
                    value={deductionForm.matchPercentage}
                    onChange={(e) => setDeductionForm({...deductionForm, matchPercentage: e.target.value})}
                    placeholder="e.g., 100"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Max Match % of Salary</Label>
                  <Input
                    type="number"
                    value={deductionForm.matchMaxPercentage}
                    onChange={(e) => setDeductionForm({...deductionForm, matchMaxPercentage: e.target.value})}
                    placeholder="e.g., 6"
                  />
                </div>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDeductionModal(false)}>
                Cancel
              </Button>
              <Button type="submit">
                {isEditing ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
